import React from "react";
import { motion } from "framer-motion";
import {
  Award,
  CheckCircle,
  Users,
  TrendingUp,
  Globe,
  Mail,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";


const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const CareersAtMBASolutions = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Users size={18} className="text-orange-400" />
            <span>Join Our Team</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Careers at MBA Solutions Pvt. Ltd.
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Join our team of experts in ISO certification consultancy. Help
            organizations achieve compliance and excellence while growing your
            career in a supportive, innovative environment.
          </p>
        </motion.div>
      </section>

      {/* Overview / Why Work with Us */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Work with Us?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              At MBA Solutions Pvt. Ltd., we offer more than just a job — we
              provide opportunities to grow, innovate, and make a real impact in
              the certification and compliance industry.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Make a Difference: Collaborate with passionate professionals helping businesses achieve certification and regulatory compliance.",
                "Collaborate & Innovate: Thrive in a dynamic environment where your ideas are valued and drive real results.",
                "Continuous Learning & Development: Access training, mentorship, and cutting-edge resources for professional growth.",
                "Work-Life Balance: Enjoy flexible schedules and a culture that prioritizes well-being.",
                "Competitive Compensation: Attractive salary, benefits, and performance incentives.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-gray-50 p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Integrity",
                desc: "We do the right thing — honesty, ethics, and transparency guide every decision.",
              },
              {
                title: "Excellence",
                desc: "We strive for the highest standards in client service, collaboration, and quality.",
              },
              {
                title: "Innovation",
                desc: "We embrace new ideas and technologies to deliver better outcomes for clients and team.",
              },
              {
                title: "Collaboration",
                desc: "The best solutions come from teamwork — internally and with our clients.",
              },
            ].map((value, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-shadow text-center"
              >
                <Award
                  size={40}
                  className="text-[var(--bg-primary)] mx-auto mb-4"
                />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Current Openings
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              We’re always seeking talented individuals. Here are our current
              opportunities (Hybrid locations):
            </p>
            <div className="space-y-8">
              {[
                {
                  number: "1",
                  title: "ISO Certification Consultant",
                  desc: "Advise clients on ISO certification, conduct internal audits, and support implementation of ISO standards.",
                  req: "Experience in ISO certification, strong analytical skills, client-facing experience.",
                },
                {
                  number: "2",
                  title: "Business Development Executive",
                  desc: "Build client relationships, identify opportunities, and expand our market presence in certification/compliance.",
                  req: "Proven sales experience, excellent communication, knowledge of certification industry.",
                },
                {
                  number: "3",
                  title: "Compliance Officer",
                  desc: "Ensure regulatory compliance for clients and develop strategies to maintain standards.",
                  req: "Experience in regulatory compliance, attention to detail, proactive approach.",
                },
                {
                  number: "4",
                  title: "Marketing Manager",
                  desc: "Lead marketing strategies, digital campaigns, SEO, content, and social media to generate leads.",
                  req: "B2B marketing experience, proficiency in SEO, content marketing, social media.",
                },
              ].map((job, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex items-start gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-xl">
                    {job.number}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {job.title}
                    </h3>
                    <p className="text-gray-600 mb-2">
                      <strong>Responsibilities:</strong> {job.desc}
                    </p>
                    <p className="text-gray-600">
                      <strong>Requirements:</strong> {job.req}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Apply */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Apply
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              If you’re passionate about certification, compliance, and making
              an impact, we’d love to hear from you!
            </p>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <p className="text-gray-700 text-lg mb-4">
                Submit your resume and a cover letter explaining why you’d be a
                great fit to:
              </p>
              <a
                href="mailto:atif@mbasolutions.co"
                className="inline-flex items-center gap-2 text-[var(--bg-primary)] hover:text-orange-600 font-semibold text-lg transition-colors"
              >
                <Mail size={20} />
                atif@mbasolutions.co
              </a>
              <p className="text-gray-600 mt-6">
                We are an equal opportunity employer and welcome applications
                from all backgrounds.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Closing Motivational Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Build Your Career with MBA Solutions
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              Join a team dedicated to excellence in ISO certification
              consultancy. Grow professionally while helping businesses
              worldwide achieve compliance and success.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We look forward to welcoming passionate professionals who share
              our values of integrity, excellence, innovation, and
              collaboration.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default CareersAtMBASolutions;
